import * as React from 'react';

import { Avatar, Grid, Paper, TextField } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import Select from 'react-select';

import { IClubOfficer } from '../../../../../../store/admin/clubs/store';
import { IAdminMembershipsStore } from '../../../../../../store/admin/memberships/store';
import { IMessageNotification } from '../../../../../../store/notification/store';
import { TPushNotificationAction } from '../../../../../../store/notification/Actions/push';

import Button from '../../../../../../components/Button';
import YesNoSwitch from '../../../../../../components/YesNoSwitch';
import { selectorTheme } from '../../../../../../contrib/utils';
import { getOfficerPermissionsValue } from '../Utils/getOfficerPermissionsValue';

export interface IProps {
  memberships: IAdminMembershipsStore;
  isLoggedOwner: boolean;
  officer: IClubOfficer;
  pushNotification: TPushNotificationAction;
  removeManagement: any;
  toggleTransferAdminDialog: () => void;
  updateManagement: any;
}

export interface IState {
  editMode: boolean;
  displayConfirm: boolean;
  position: string;
}

export default class OfficerRow extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = { displayConfirm: true, position: '', editMode: false };
  }

  public setAdminPermission = (value: any) => {
    const {
      updateManagement,
      officer: { id: officerId, club: clubId }
    } = this.props;

    const { label } = value;

    const isStaff = label === 'STAFF' ? true : false;
    const isAdmin = label === 'ADMIN' ? true : false;

    updateManagement(clubId, officerId, {
      isAdmin,
      isStaff
    });
  };

  public toggleActiveOfficer = (_: any, value: boolean) => {
    const {
      updateManagement,
      officer: { id: officerId, club: clubId }
    } = this.props;

    updateManagement(clubId, officerId, {
      status: value ? 'active' : 'inactive'
    });
  };

  public componentDidMount = () => {
    const {
      officer: { position }
    } = this.props;

    this.setState({ position });
  };

  public onPositionChange = (event: any) => {
    this.setState({ position: event.target.value });
  };

  public setEditMode = () => {
    this.setState({ editMode: true });
  };

  public cancelEditMode = () => {
    const {
      officer: { position }
    } = this.props;

    this.setState({ editMode: false, position });
  };

  public updateManagement = async () => {
    const {
      updateManagement,
      officer: { id: officerId, club: clubId }
    } = this.props;

    const { position } = this.state;

    await updateManagement(clubId, officerId, {
      position
    });

    this.setState({ editMode: false });
  };

  public removeAction = async () => {
    const {
      officer: { id: officerId, club: clubId },
      pushNotification,
      removeManagement
    } = this.props;
    const { displayConfirm } = this.state;

    if (displayConfirm) {
      this.setState({ displayConfirm: false });
    } else {
      const response = await removeManagement(clubId, officerId);

      if (response.errors) {
        await pushNotification(({
          status: 'unread',
          text: response.errors.adminClubs.msg,
          type: 'error'
        } as unknown) as IMessageNotification);
      } else {
        await pushNotification(({
          status: 'unread',
          text: 'Successfully removed',
          type: 'success'
        } as unknown) as IMessageNotification);
      }
    }
  };

  public render(): JSX.Element {
    const {
      isLoggedOwner,
      officer,
      officer: {
        user: {
          firstName,
          lastName,
          profile: { image }
        },
        isOwner,
        status
      },
      memberships: { memberships },
      toggleTransferAdminDialog
    } = this.props;

    const { editMode, displayConfirm, position } = this.state;

    const fullName = `${firstName} ${lastName}`;

    return (
      <Paper elevation={2} className="paper row officer-row">
        <Grid className="grid" container={true} spacing={0}>
          <Grid className="grid-item" item={true} xs={12} md={1}>
            <div className="item-container">
              <Avatar alt="avatar" src={image} />
            </div>
          </Grid>
          <Grid className="grid-item" item={true} xs={12} md={2}>
            <div className="item-container">{fullName}</div>
          </Grid>
          <Grid className="grid-item" item={true} xs={12} md={2} onClick={this.setEditMode}>
            <div className="item-container">
              {editMode ? (
                <TextField
                  autoFocus={true}
                  className="text-field"
                  defaultValue={position}
                  inputProps={{ 'aria-label': 'bare' }}
                  margin="normal"
                  onChange={this.onPositionChange}
                  type={'text'}
                />
              ) : (
                <>
                  <span>{position ? position.substring(0, 20) : ''}</span>
                  <EditIcon className="edit-icon" />
                </>
              )}
            </div>
          </Grid>
          <Grid className="grid-item" item={true} xs={12} md={2}>
            <YesNoSwitch
              disabled={isOwner}
              checked={status === 'active'}
              onChange={this.toggleActiveOfficer}
            />
          </Grid>
          <Grid className="grid-item" item={true} xs={12} md={2}>
            <div className="item-container">
              <Select
                isDisabled={isOwner}
                className="autocomplete"
                classNamePrefix="autocomplete"
                value={getOfficerPermissionsValue(officer)}
                onChange={this.setAdminPermission}
                options={[
                  { label: 'ADMIN', value: 'admin' },
                  { label: 'STAFF', value: 'staff' },
                  { label: 'NONE', value: 'none' }
                ]}
                theme={selectorTheme}
              />
            </div>
          </Grid>
          <Grid className="grid-item" item={true} xs={12} md={3}>
            {editMode ? (
              <div className="item-container editModeButtons">
                <Button
                  id="create-event-submit"
                  variant="outlined"
                  color="primary"
                  type="button"
                  fullWidth={true}
                  onClick={this.updateManagement}
                >
                  Save
                </Button>
                <Button
                  id="create-event-submit"
                  variant="contained"
                  color="primary"
                  type="button"
                  fullWidth={true}
                  onClick={this.cancelEditMode}
                >
                  Cancel
                </Button>
              </div>
            ) : isOwner ? (
              memberships.length > 0 ? (
                <Button
                  id="create-event-submit"
                  variant="outlined"
                  color="primary"
                  type="submit"
                  fullWidth={true}
                  onClick={toggleTransferAdminDialog}
                  disabled={!isLoggedOwner}
                >
                  Transfer Club Admin
                </Button>
              ) : (
                <div />
              )
            ) : (
              <Button
                id="create-event-submit"
                variant={displayConfirm ? 'outlined' : 'contained'}
                color="primary"
                type="submit"
                fullWidth={true}
                onClick={this.removeAction}
              >
                {displayConfirm ? 'Remove' : 'Confirm Remove'}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>
    );
  }
}
